/**
 * LoadingScreen.js — post-Summary Loading Screen (UC-02, step 2-7)
 *
 * Not one of the 12 numbered wizard screens, so it deliberately doesn't
 * use AssessmentStepShell — no step counter, no Previous/Next footer,
 * just a full-screen transitional state between Summary and the (not yet
 * built) Diet Plan Screen.
 *
 * Behavior per FSD:
 *  - Sequential progress messages ("Analyzing your lifestyle...",
 *    "Calculating nutrition...", "Preparing your personalized meal
 *    plan...") synced to backend processing stages, with an animated
 *    progress bar. Estimated duration: 20-30 seconds.
 *  - If generation exceeds 45 seconds, show a reassurance message
 *    ("Still working on your plan...") rather than appearing frozen.
 *  - UC-02 Exception Flow: if it exceeds 90 seconds, offer a retry
 *    button. On an explicit failure, show a friendly retry option
 *    immediately rather than waiting for the 90s mark.
 *
 * generatePlan's remaining UC-02 steps (Nutrition Database filtering, AI
 * meal selection, validation) aren't built yet — there's no API contract
 * for them in what's been specified so far. Step 3 (BMI/BMR/TDEE calc)
 * IS now real, done client-side in LoadingScreen.helpers.js from the
 * user's actual basicInfo/activity/goal. This component takes an
 * `onGenerate(payload)` prop that must return a
 * Promise resolving to the finalized plan, and falls back to a local
 * mock (DEFAULT_MOCK_DELAY_MS below) so the screen is fully demoable
 * before that integration exists. Swap in the real call by passing
 * `onGenerate={callGenerateDietPlanApi}` from DietPlannerWizard.js —
 * nothing else in this file needs to change.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import useLoadingAnalytics from "./useLoadingAnalytics";
import {
  PROGRESS_MESSAGES,
  SIMULATED_DURATION_MS,
  TARGET_PROGRESS_CAP,
  MESSAGE_INTERVAL_MS,
  PROGRESS_TICK_MS,
  REASSURANCE_THRESHOLD_MS,
  REASSURANCE_MESSAGE,
  RETRY_OFFER_THRESHOLD_MS,
  RETRY_OFFER_MESSAGE,
  ERROR_MESSAGE,
} from "./LoadingScreen.constants";
import {
  Screen,
  Spinner,
  Message,
  ProgressTrack,
  ProgressFill,
  ProgressPercent,
  ReassuranceText,
  ErrorPanel,
  ErrorText,
  RetryButton,
} from "./LoadingScreen.styles";
// MealCatalog.js/MealEngine.js live in a sibling folder to Loading/ (e.g.
// src/components/sections/MealEngine/) — adjust the path below to match
// wherever they actually end up in the project.
import { MEAL_SLOT_ORDER } from "../MealEngine/MealCatalog";
import {
  buildMealContext,
  buildEffectiveHealthSummary,
  buildDailyTips,
  pickMealsForSlots,
  computeSlotTargets,
  buildScaledMealSlot,
} from "../MealEngine/MealEngine";

// TODO: replace with a real call once UC-02's generation endpoint exists
// (steps 4-6: Nutrition Database filtering, AI meal selection,
// validation — step 3's BMI/BMR/TDEE calc and the meal-selection logic
// itself are now real, see MealEngine.js). Resolves after a few seconds
// with a mock plan shaped the way DietPlanScreen.js expects, so the full
// flow — Loading → Diet Plan Screen — is demoable end to end before that
// backend exists.
const DEFAULT_MOCK_DELAY_MS = 4000;

// Screen 10's "Number of Meals" is optional and free-form-ish (whatever
// NUM_MEALS_OPTIONS' ids look like), so this pulls out the first number it
// finds rather than assuming an exact string shape, and clamps to what the
// mock's catalog can actually cover (3-6). No value at all defaults to the
// original 3-meal behavior.
function resolveMealCount(payload) {
  const raw = payload?.mealSchedule?.numMeals;
  const match = raw != null ? String(raw).match(/\d+/) : null;
  const parsed = match ? parseInt(match[0], 10) : NaN;
  if (!Number.isFinite(parsed)) return 3;
  return Math.min(6, Math.max(3, parsed));
}

// This previously always returned exactly Breakfast/Lunch/Dinner (+
// Bedtime Snack when there was no fasting window) regardless of what the
// user picked for "Number of Meals" on Screen 10 — so selecting 6 meals
// still produced a 3-4 meal plan. This scales the snack slots in to match
// the requested count, while keeping the Fasting Window override: a
// fasting window always suppresses the Bedtime Snack, taking precedence
// over Number of Meals per the conditional-display note in
// MealScheduleStep.js's header.
function resolveMealSlotIds(payload) {
  const hasFastingWindow = Boolean(payload?.mealSchedule?.fastingWindow);
  const count = resolveMealCount(payload);
  const extrasNeeded = Math.max(0, count - 3);
  const extraPool = ["mid_morning_snack", "afternoon_snack", "bedtime_snack"].filter(
    (id) => !(hasFastingWindow && id === "bedtime_snack")
  );
  const extras = new Set(extraPool.slice(0, extrasNeeded));
  const included = new Set(["breakfast", "lunch", "dinner", ...extras]);
  return MEAL_SLOT_ORDER.filter((id) => included.has(id));
}

function resolveScheduledTime(slotId, payload) {
  // mealSchedule only carries explicit times for breakfast/lunch/dinner/
  // bedtime — snack slots added to hit a higher meal count don't have a
  // user-entered time, so buildScaledMealSlot falls back to the chosen
  // dish's own default time in that case.
  return payload?.mealSchedule?.[slotId] ?? payload?.mealSchedule?.[slotId.replace("_snack", "")] ?? null;
}

/**
 * Builds a plan that's actually shaped by who the user is, not the same
 * fixed dishes for everyone:
 *  - MealEngine.buildMealContext() reads diet type, cuisine, dislikes/
 *    likes, medical conditions (gluten, plan-blocked), and fasting window.
 *  - pickMealsForSlots() hard-filters MealCatalog.js's candidates by diet
 *    type/gluten/dislikes and soft-ranks by cuisine/likes, per FSD v2.1 §6.
 *  - computeSlotTargets()/buildScaledMealSlot() size each chosen dish
 *    toward the user's real calorie/protein targets instead of a fixed
 *    calorie number per dish.
 *  - buildDailyTips() reflects goal/conditions/fasting window instead of
 *    the same 3 tips for every session.
 * Still not the full Rule Engine/Nutrition Database (no cooking-time/
 * budget ranking yet, no pregnancy-trimester-adjusted targets, no real
 * ingredient nutrition database) — see MealEngine.js's header for what's
 * still mocked.
 */
function buildMockPlan(payload) {
  const ctx = buildMealContext(payload);
  const slotIds = resolveMealSlotIds(payload);
  const picked = pickMealsForSlots(slotIds, ctx);
  const pickedSlotIds = Object.keys(picked); // preserves slotIds' order; may omit a slot with zero eligible dishes

  const healthSummary = buildEffectiveHealthSummary(payload);
  const targets = computeSlotTargets(pickedSlotIds, healthSummary.calories, healthSummary.proteinG);

  const mealSlots = pickedSlotIds.map((slotId) =>
    buildScaledMealSlot(slotId, picked[slotId], targets[slotId], resolveScheduledTime(slotId, payload))
  );

  return {
    generatedAt: new Date().toISOString(),
    sessionId: payload?.sessionId ?? null,
    isMock: true,
    riskFlag: ctx.riskFlag,
    healthSummary,
    mealSlots,
    dailyTips: buildDailyTips(ctx),
  };
}

function defaultMockGenerate(payload) {
  return new Promise((resolve) => {
    setTimeout(() => resolve(buildMockPlan(payload)), DEFAULT_MOCK_DELAY_MS);
  });
}

/**
 * @param {object}   props
 * @param {string}   props.sessionId - Anonymous session id from Screen 1.
 * @param {object}   props.payload   - Everything collected across
 *   Screens 2-11, handed straight through to onGenerate.
 * @param {function} [props.onGenerate] - `(payload) => Promise<plan>`.
 *   Defaults to a local mock (see defaultMockGenerate above) so this
 *   screen works before the real endpoint exists.
 * @param {function} props.onSuccess - Called with the resolved plan once
 *   generation finishes; DietPlannerWizard.js uses this to advance to the
 *   Diet Plan Screen.
 */
const LoadingScreen = ({ sessionId, payload, onGenerate, onSuccess }) => {
  const analytics = useLoadingAnalytics();

  const [attempt, setAttempt] = useState(0);
  const [phase, setPhase] = useState("generating"); // "generating" | "error"
  const [elapsedMs, setElapsedMs] = useState(0);
  const [simulatedProgress, setSimulatedProgress] = useState(0);
  const [messageIndex, setMessageIndex] = useState(0);
  const [errorMessage, setErrorMessage] = useState(null);

  // Guards against firing the reassurance/retry-offer analytics events
  // more than once per attempt.
  const reassuranceFiredRef = useRef(false);
  const retryOfferFiredRef = useRef(false);

  useEffect(() => {
    analytics.trackStepView(sessionId);
    // Only fire the page-view event once, on mount — intentionally
    // excluded from deps below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    reassuranceFiredRef.current = false;
    retryOfferFiredRef.current = false;
    setPhase("generating");
    setElapsedMs(0);
    setSimulatedProgress(0);
    setMessageIndex(0);
    setErrorMessage(null);

    const startedAt = Date.now();

    const tick = setInterval(() => {
      const elapsed = Date.now() - startedAt;
      setElapsedMs(elapsed);
      setSimulatedProgress(Math.min(TARGET_PROGRESS_CAP, (elapsed / SIMULATED_DURATION_MS) * TARGET_PROGRESS_CAP));
      setMessageIndex(Math.min(PROGRESS_MESSAGES.length - 1, Math.floor(elapsed / MESSAGE_INTERVAL_MS)));

      if (!reassuranceFiredRef.current && elapsed >= REASSURANCE_THRESHOLD_MS) {
        reassuranceFiredRef.current = true;
        analytics.trackReassuranceShown(sessionId, elapsed);
      }
      if (!retryOfferFiredRef.current && elapsed >= RETRY_OFFER_THRESHOLD_MS) {
        retryOfferFiredRef.current = true;
        analytics.trackRetryOfferShown(sessionId, elapsed);
      }
    }, PROGRESS_TICK_MS);

    const generate = onGenerate ?? defaultMockGenerate;

    generate({ sessionId, ...payload })
      .then((plan) => {
        if (cancelled) return;
        clearInterval(tick);
        setSimulatedProgress(100);
        analytics.trackGenerationSuccess(sessionId, Date.now() - startedAt, attempt);
        // Brief pause so the bar visibly reaches 100% before the screen
        // changes, rather than jump-cutting away mid-animation.
        setTimeout(() => {
          if (!cancelled) onSuccess?.(plan);
        }, 500);
      })
      .catch((err) => {
        if (cancelled) return;
        clearInterval(tick);
        setPhase("error");
        setErrorMessage(err?.message || ERROR_MESSAGE);
        analytics.trackGenerationFailure(sessionId, Date.now() - startedAt, attempt, err?.message);
      });

    return () => {
      cancelled = true;
      clearInterval(tick);
    };
    // payload/onGenerate/onSuccess/sessionId/analytics are effectively
    // stable for the lifetime of this screen; `attempt` is the only thing
    // meant to re-trigger this effect (via handleRetry below).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt]);

  const handleRetry = useCallback(() => {
    analytics.trackRetryClick(sessionId, attempt + 1);
    setAttempt((a) => a + 1);
  }, [analytics, sessionId, attempt]);

  const showReassurance = phase === "generating" && elapsedMs >= REASSURANCE_THRESHOLD_MS;
  const showRetryOffer = phase === "generating" && elapsedMs >= RETRY_OFFER_THRESHOLD_MS;
  const showErrorRetry = phase === "error";

  return (
    <Screen role="status" aria-live="polite">
      <Spinner aria-hidden="true" />
      <Message>{PROGRESS_MESSAGES[messageIndex]}</Message>
      <ProgressTrack aria-hidden="true">
        <ProgressFill $percent={simulatedProgress} />
      </ProgressTrack>
      <ProgressPercent>{Math.round(simulatedProgress)}%</ProgressPercent>

      {showReassurance && !showRetryOffer && <ReassuranceText>{REASSURANCE_MESSAGE}</ReassuranceText>}

      {(showRetryOffer || showErrorRetry) && (
        <ErrorPanel>
          <ErrorText>{showErrorRetry ? errorMessage : RETRY_OFFER_MESSAGE}</ErrorText>
          <RetryButton type="button" onClick={handleRetry}>
            Try again
          </RetryButton>
        </ErrorPanel>
      )}
    </Screen>
  );
};

LoadingScreen.displayName = "LoadingScreen";
export default React.memo(LoadingScreen);